<template>
  <div class="solution-progress">
    <div class="agent-line">
      <span class="agent-avatar"><v-icon size="17" color="#2169A8">mdi-lightning-bolt</v-icon></span>
      <div>
        <div class="agent-name">Schema solution agent</div>
        <p class="agent-say">{{ introLine }}</p>
      </div>
    </div>

    <div class="steps-wrap">
      <div class="steps-card">
        <div
          v-for="(step, index) in pacedSteps"
          :key="step.label"
          class="step-row"
          :class="[`is-${step.state}`, { 'is-first': index === 0 }]"
        >
          <div class="step-head">
            <span class="step-mark" :class="`mark-${step.state}`">
              <v-icon v-if="step.state === 'done'" size="11" color="#127a6a">mdi-check</v-icon>
            </span>
            <span class="step-label">{{ step.label }}</span>
          </div>
          <div v-for="detail in step.details" :key="detail" class="step-detail">{{ detail }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { agentIntroLineFor } from '@/utils/solutionSchema'

// A step the agent finishes in milliseconds still reads as a step. Hold each one visibly
// before ticking it, so the checklist advances at a pace a reader can follow.
const MIN_STEP_MS = 1500

export default {
  name: 'SchemaSolutionProgress',
  props: {
    steps: {
      type: Array,
      default: () => []
    },
    pageCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      revealed: 0,
      holdTimer: null
    }
  },
  computed: {
    introLine() {
      return agentIntroLineFor(this.pageCount)
    },
    doneCount() {
      return this.steps.filter((step) => step.state === 'done').length
    },
    // Never shows a step further along than the run actually is; only ever holds it back.
    pacedSteps() {
      return this.steps.map((step, index) => {
        if (index < this.revealed) return { ...step, state: 'done' }
        if (index === this.revealed && step.state !== 'pending') return { ...step, state: 'active' }
        return { ...step, state: 'pending' }
      })
    }
  },
  watch: {
    doneCount() {
      this.advanceWhenBehind()
    }
  },
  mounted() {
    // A run that was already finished when the page opened has nothing to animate.
    this.revealed = this.doneCount
  },
  beforeUnmount() {
    if (this.holdTimer) clearTimeout(this.holdTimer)
  },
  methods: {
    advanceWhenBehind() {
      if (this.holdTimer || this.revealed >= this.doneCount) return
      this.holdTimer = setTimeout(() => {
        this.holdTimer = null
        this.revealed += 1
        this.advanceWhenBehind()
      }, MIN_STEP_MS)
    }
  }
}
</script>

<style scoped>
.agent-line {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.agent-avatar {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--brand-ocean-100);
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-name {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 2px;
}

.agent-say {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  max-width: 68ch;
}

.steps-wrap {
  margin-top: 14px;
  padding-left: 42px;
  max-width: 720px;
}

.steps-card {
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  padding: 16px;
}

.step-row {
  padding: 9px 0;
  transition: opacity 0.25s ease;
}

.step-row.is-first {
  padding-top: 0;
}

.step-row.is-pending {
  opacity: 0.38;
}

.step-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-mark {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mark-done {
  background: var(--brand-teal-100);
}

.mark-active {
  border: 2px solid var(--brand-ocean-100);
  border-top-color: var(--brand-ocean-600);
  animation: solution-step-spin 0.8s linear infinite;
}

.mark-pending {
  border: 1px solid var(--brand-sand-300);
}

.step-label {
  font-size: 14px;
  font-weight: 600;
}

.step-row.is-pending .step-label {
  color: #8496bc;
}

.step-detail {
  margin-left: 28px;
  margin-top: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  color: var(--brand-ink-800);
  overflow-wrap: anywhere;
}

@keyframes solution-step-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
